import { getRoles, createInvitation } from "@/services/user-service";
import { useUserStore } from "@/stores";
import { Modal, Form, Input, Select, message, Spin, Alert, Typography } from "antd";
import { useState, useEffect } from "react";

const { Text, Paragraph } = Typography;

interface InviteUserModalProps {
	open: boolean;
	onCancel: () => void;
	onSuccess: () => void;
}

const InviteUserModal = ({ open, onCancel, onSuccess }: InviteUserModalProps) => {
	const currentUser = useUserStore((state: any) => state.user);
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [inviteLink, setInviteLink] = useState<string | null>(null);
	const [roles, setRoles] = useState<any[]>([]);
	const [isRolesLoading, setIsRolesLoading] = useState(false);
	const [getRolesErr, setGetRolesErr] = useState<string | null>(null);

	useEffect(() => {
		if (open && roles.length === 0) {
			setIsRolesLoading(true);
			getRoles()
				.then((data) => {
					setRoles(data);
					setGetRolesErr(null);
				})
				.catch((err) => {
					setGetRolesErr(err.message || "Failed to load roles");
				})
				.finally(() => setIsRolesLoading(false));
		}
	}, [open]);

	const handleSubmit = async () => {
		try {
			const values = await form.validateFields();
			setLoading(true);

			const invitation = await createInvitation(values.email, values.role_id);

			// Generate invite link
			const baseUrl = window.location.origin;
			const link = `${baseUrl}/accept-invitation/${invitation.token}`;
			setInviteLink(link);

			message.success("Invitation created successfully");
		} catch (error: any) {
			console.error("Error creating invitation:", error);
			message.error(error.message || "Failed to create invitation");
		} finally {
			setLoading(false);
		}
	};

	const handleCancel = () => {
		form.resetFields();
		setInviteLink(null);
		onCancel();
	};

	const handleCopyLink = () => {
		if (inviteLink) {
			navigator.clipboard.writeText(inviteLink);
			message.success("Invitation link copied to clipboard");
		}
	};

	const handleDone = () => {
		form.resetFields();
		setInviteLink(null);
		onSuccess();
	};

	// Filter roles based on current user's access level
	const getAvailableRoles = () => {
		if (!roles) return [];
		const currentAccessLevel = currentUser?.role?.accessLevel || currentUser?.role?.access_level || 0;
		
		return roles.filter((role: any) => {
			const roleAccessLevel = role.accessLevel || role.access_level;
			// Can only invite for roles up to their own level
			return roleAccessLevel <= currentAccessLevel;
		});
	};

	const availableRoles = getAvailableRoles();

	return (
		<Modal
			title="Invite User"
			open={open}
			onOk={inviteLink ? handleDone : handleSubmit}
			onCancel={handleCancel}
			okText={inviteLink ? "Done" : "Send Invitation"}
			cancelText={inviteLink ? "Invite Another" : "Cancel"}
			confirmLoading={loading}
			destroyOnClose
		>
			{isRolesLoading ? (
				<div style={{ textAlign: "center", padding: "20px" }}>
					<Spin />
				</div>
			) : inviteLink ? (
				<div>
					<Alert
						message="Invitation Created"
						description="Share this link with the user to complete their registration."
						type="success"
						showIcon
						style={{ marginBottom: 16 }}
					/>
					<div style={{ 
						background: "#f5f5f5", 
						padding: "12px", 
						borderRadius: "6px",
						marginBottom: 16 
					}}>
						<Text code style={{ wordBreak: "break-all" }}>{inviteLink}</Text>
					</div>
					<Paragraph type="secondary">
						This invitation will expire in 7 days. The user will need to create a password when they accept.
					</Paragraph>
					<div style={{ textAlign: "center" }}>
						<a onClick={handleCopyLink} style={{ cursor: "pointer" }}>
							Copy Link
						</a>
					</div>
				</div>
			) : (
				<Form
					form={form}
					layout="vertical"
					initialValues={{
						role_id: availableRoles.find((r: any) => r.name === "USER")?.id || availableRoles[0]?.id,
					}}
				>
					<Form.Item
						name="email"
						label="Email Address"
						rules={[
							{ required: true, message: "Please enter an email address" },
							{ type: "email", message: "Please enter a valid email address" },
						]}
					>
						<Input placeholder="user@example.com" />
					</Form.Item>

					<Form.Item
						name="role_id"
						label="Role"
						rules={[{ required: true, message: "Please select a role" }]}
					>
						{getRolesErr ? (
							<div style={{ color: "red" }}>Failed to load roles</div>
						) : (
							<Select placeholder="Select a role">
								{availableRoles.map((role: any) => (
									<Select.Option key={role.id} value={role.id}>
										{role.name}
									</Select.Option>
								))}
							</Select>
						)}
					</Form.Item>

					<Alert
						message="How it works"
						description="The user will receive a link to complete their registration. They'll need to create a password and can then sign in."
						type="info"
						showIcon
					/>
				</Form>
			)}
		</Modal>
	);
};

export { InviteUserModal };
